
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { changePassword } from '@/contexts/auth/services/passwordService';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { UserRoles } from '@/contexts/auth/enums';

const passwordSchema = z.object({
  currentPassword: z.string().min(1, { message: 'Jelenlegi jelszó megadása kötelező' }),
  newPassword: z.string().min(8, { message: 'Az új jelszó legalább 8 karakter hosszú legyen' }),
  confirmPassword: z.string().min(8, { message: 'A jelszó megerősítése kötelező' }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "A jelszavak nem egyeznek",
  path: ["confirmPassword"],
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

const ChangePassword = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: PasswordFormValues) => {
    setIsLoading(true);
    try {
      const response = await changePassword(data.currentPassword, data.newPassword);

      if (response.success) {
        toast({
          title: "Sikeres jelszóváltoztatás",
          description: "A jelszava sikeresen megváltozott.",
          variant: "default",
        });

        // Redirect to profile page after successful password change
        if (user?.roles.includes(UserRoles.ORGANIZER)) {
          navigate('/organizer/profile');
        } else {
          navigate('/participant/profile');
        }
      } else {
        toast({
          title: "Sikertelen jelszóváltoztatás",
          description: response.message || "Hiba történt a jelszó megváltoztatása során.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Password change error:', error);
      toast({
        title: "Hiba történt",
        description: "Nem sikerült megváltoztatni a jelszót. Kérjük, próbálja újra később.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex  flex-col">
      <Navbar />
      <div className="flex-1 mt-16">
        <div className="container max-w-md py-12">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold">Jelszó változtatás</CardTitle>
              <CardDescription>
                Adja meg jelenlegi jelszavát és az új jelszavát a módosításhoz
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Jelenlegi jelszó</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Jelenlegi jelszó" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Új jelszó</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Új jelszó" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Új jelszó megerősítése</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Írja be újra az új jelszót" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Feldolgozás...' : 'Jelszó megváltoztatása'}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
